import { Injectable } from '@angular/core';
// rxjs
import { BehaviorSubject, Observable } from 'rxjs';
// services
import { RestaurantService } from '../services/restaurant.service';
import { GiftCardImage, LoyaltyProgram, Merchant, MerchantMobileApp, MobileAppHomepageTile, MobileAppMenuTab, RewardTiers } from '../models/restaurant.model';
import { MerchantService } from '../services/merchant.service';
import { CartStore } from './cart.store';
import { RestaurantStore } from './restaurant.store';
import { RestaurantProviderOnly } from '../directive/restaurant-provider.decorator';
import { SERVICE_PROVIDER } from '../enums/merchant.enum';
import { dataToLoyaltyProgram } from '../dto/restaurant.dto';
import { dataToMerchantInfo, dataToMerchantMobileAppInfo, dataToMobileAppHomepageTile, dataToMobileAppMenuTab } from '../dto/merchant.dto';

@Injectable()
export class MerchantStore {
  private _merchantData$ = new BehaviorSubject<Merchant>(null);
  private _merchant$ = new BehaviorSubject<Merchant>(null);
  private _merchantIsAvailable$ = new BehaviorSubject<boolean>(true);
  private _loyaltyProgram$ = new BehaviorSubject<any>(null);
  private _merchantMobileApp$ = new BehaviorSubject<MerchantMobileApp>(null);
  private _merchantGiftCardImages$ = new BehaviorSubject<GiftCardImage[]>([]);
  private _mobileAppHomepageTiles$ = new BehaviorSubject<MobileAppHomepageTile[]>([]);
  private _mobileAppMenuTabs$ = new BehaviorSubject<MobileAppMenuTab[]>([]);
  private _catalogItemIcons$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private _notifications$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private _subscriptionGroups$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private _kyooPromos$: BehaviorSubject<any> = new BehaviorSubject<any>([]);

  public get merchantMobileApp$(): Observable<MerchantMobileApp> {
    return this._merchantMobileApp$.asObservable();
  }

  public get merchantGiftCardImages$(): Observable<GiftCardImage[]> {
    return this._merchantGiftCardImages$.asObservable();
  }

  public get mobileAppHomepageTiles$(): Observable<MobileAppHomepageTile[]> {
    return this._mobileAppHomepageTiles$.asObservable();
  }

  public get mobileAppMenuTabs$(): Observable<MobileAppMenuTab[]> {
    return this._mobileAppMenuTabs$.asObservable();
  }

  public get merchantData$(): Observable<Merchant> {
    return this._merchantData$.asObservable();
  }

  public get merchantIsAvailable$(): Observable<boolean> {
    return this._merchantIsAvailable$.asObservable();
  }

  public get loyaltyProgram$(): Observable<LoyaltyProgram> {
    return this._loyaltyProgram$.asObservable();
  }

  public getLoyaltyProgramValue(): LoyaltyProgram | null {
    return this._loyaltyProgram$.getValue();
  }

  public get catalogItemIcons$(): Observable<any> {
    return this._catalogItemIcons$;
  }

  public get notifications$(): Observable<any> {
    return this._notifications$;
  }

  public get subscriptionGroups$(): Observable<any> {
    return this._subscriptionGroups$;
  }

  public get kyooPromos$(): Observable<any> {
    return this._kyooPromos$;
  }

  public getSubGroups(): any[] {
    return this._subscriptionGroups$.getValue()
  }


  constructor(
    private merchantService: MerchantService,
    private restaurantService: RestaurantService,
    private cartStore: CartStore,
    private restaurantStore: RestaurantStore
  ) {
  }

  public bootstrapMerchant(id: string): void {
    this.restaurantService.getMerchantById(id).subscribe(
      (merchant) => {
        this._merchant$.next(merchant);
        this.setMerchantIsAvailable(merchant.available);
      },
      () => {
        this.setMerchantIsAvailable(false);
      }
    );
  }

  public getMerchantData(merchantId: string): void {
    this.merchantService.getMerchantData(merchantId).subscribe((merchant) => {
      this._merchantData$.next(merchant);
    });
  }

  public setMerchantIsAvailable(value: boolean) {
    this._merchantIsAvailable$.next(value);
  }

  public getLoyaltyProgram(merchantId: string): void {
      this.merchantService.getLoyaltyProgram(merchantId).subscribe((loyalty) => {
        this._loyaltyProgram$.next(loyalty);
        this.cartStore.isLoyaltyProgramEnabled = true;
      });
  }

  public getMerchantMobileAppData(merchantId: string): void {
    this.merchantService.getMerchantMobileApp(merchantId).subscribe((merchantMobileApp) => {
      this._merchantMobileApp$.next(merchantMobileApp);
    });
  }

  // Fetch gift card images
  public getGiftCardImages(merchantId: string): void {
    this.merchantService.getMerchantGiftCardImages(merchantId).subscribe((merchantGiftCardImages) => {
      merchantGiftCardImages.sort((a, b) => (b.defaultImage ? 1 : 0) - (a.defaultImage ? 1 : 0));
      this._merchantGiftCardImages$.next(merchantGiftCardImages);
    });
  }

  public getMobileAppHomepageTiles(merchantId: string): void {
    this.merchantService.getMobileAppHomepageTiles(merchantId).subscribe((mobileAppHomepageTiles) => {
      this._mobileAppHomepageTiles$.next(mobileAppHomepageTiles);
    })
  }

  public getMobileMenuTabs(merchantId: string): void {
    this.merchantService.getMobileAppMenuTabs(merchantId).subscribe((mobileAppMenuTabs) => {
      this._mobileAppMenuTabs$.next(mobileAppMenuTabs);
    })
  }

  public getMerchantCatalogItemIcons(merchantId: string): void {
    this.merchantService.getMerchantCatalogItemIcons(merchantId).
      subscribe((response) => {
        this._catalogItemIcons$.next(response);
      });
  }

  public getMerchantNotifications(merchantId: string): void {
    this.merchantService.getMerchantNotifications(merchantId).
    subscribe((response) => {
      this._notifications$.next(response)
    })
  }

  public getSubscriptionGroups(merchantId: string): void {
    this.merchantService.getMerchantPrivateSubscriptions(merchantId).
    subscribe((response) => {
      this._subscriptionGroups$.next(response)
    })
  }

  public getKyooPromos(merchaintId: string): void {
    this.merchantService.getMerchantKyooPromos(merchaintId).
    subscribe((response) => {
      this._kyooPromos$.next(response);
    })
  }

  public bootstrapRestaurantData(merchantId: string): void {
    this.bootstrapMerchant(merchantId);
    this.restaurantStore.bootstrapRestaurants(merchantId);
    this.getMerchantData(merchantId);
    this.getLoyaltyProgram(merchantId);
  }

  public bootstrapMobileData(merchantId: string): void {
    this.getMerchantMobileAppData(merchantId);
    this.getMobileAppHomepageTiles(merchantId);
    this.getMobileMenuTabs(merchantId);
  }

  public bootstrapOptionalMobileData(merchantId: string): void {
    this.getGiftCardImages(merchantId);
    this.getSubscriptionGroups(merchantId);
    this.getKyooPromos(merchantId);
  }

  /**
   * Combined bootstrap method - replaces multiple API calls with single request
   * This method fetches all merchant and mobile app data in one call
   * Use this instead of bootstrapMobileData() + bootstrapRestaurantData()
   */
  public bootstrapCombined(merchantId: string): void {
    this.merchantService.getBootstrapData(merchantId).subscribe((data: any) => {
      // Populate merchant data
      if (data.merchant) {
        this._merchant$.next(data.merchant);
        this.setMerchantIsAvailable(data.merchant.available);
      }

      // Populate merchant info (apply DTO transformation)
      if (data.merchant_info) {
        const merchantInfo = dataToMerchantInfo(data.merchant_info, merchantId);
        this._merchantData$.next(merchantInfo);
      }

      // Populate loyalty program (apply DTO transformation)
      if (data.loyalty_program) {
        const loyaltyProgram = dataToLoyaltyProgram(data.loyalty_program);
        this._loyaltyProgram$.next(loyaltyProgram);
        this.cartStore.isLoyaltyProgramEnabled = true;
      }

      // Populate mobile app data (optional - may be null, apply DTO transformation)
      if (data.mobile_app) {
        const mobileApp = dataToMerchantMobileAppInfo(data.mobile_app, merchantId);
        this._merchantMobileApp$.next(mobileApp);
      }

      if (data.homepage_tiles && data.homepage_tiles.length > 0) {
        const tiles = data.homepage_tiles.map(dataToMobileAppHomepageTile);
        this._mobileAppHomepageTiles$.next(tiles);
      }

      if (data.menu_tabs && data.menu_tabs.length > 0) {
        const tabs = data.menu_tabs.map(dataToMobileAppMenuTab);
        this._mobileAppMenuTabs$.next(tabs);
      }

      // Note: Restaurants/locations and categories are handled separately by restaurantStore
    }, (error) => {
      console.error('Bootstrap combined failed:', error);
      this.setMerchantIsAvailable(false);
    });
  }

   public clearMerchant(): void {
    this._merchant$.next(null);
    this._merchantData$.next(null);
  }

}
